/**
 * Configuração de runtime - carregada do config.json
 * 
 * Permite atualizar configurações sem rebuild do frontend.
 * O config.json é gerado em runtime pelo docker-entrypoint.sh
 * a partir de variáveis de ambiente (AWS Secrets Manager/Parameter Store).
 */

export interface RuntimeConfig {
  apiUrl: string;
  apiToken: string;
}

// Cache da configuração carregada
let cachedConfig: RuntimeConfig | null = null;
let configLoadPromise: Promise<RuntimeConfig> | null = null;

// Valores padrão para desenvolvimento
const DEFAULT_CONFIG: RuntimeConfig = {
  apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  apiToken: process.env.REACT_APP_API_TOKEN || '',
};

/**
 * Carrega a configuração do config.json (runtime) ou usa variáveis de ambiente (build-time)
 * 
 * Prioridade:
 * 1. config.json (produção - gerado em runtime)
 * 2. Variáveis de ambiente REACT_APP_* (desenvolvimento/build-time)
 * 3. Valores padrão
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Se já temos config em cache, retornar
  if (cachedConfig) {
    return cachedConfig;
  }

  // Evitar múltiplas requisições simultâneas
  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = (async () => {
    try {
      // Tentar carregar config.json em produção
      const response = await fetch('/config.json', {
        cache: 'no-store', // Sempre buscar versão atualizada
      });

      if (response.ok) {
        const config = await response.json();
        
        // Validar que não são os placeholders do template
        if (config.apiUrl && !config.apiUrl.includes('__REACT_APP')) {
          cachedConfig = {
            apiUrl: config.apiUrl || DEFAULT_CONFIG.apiUrl,
            apiToken: config.apiToken || DEFAULT_CONFIG.apiToken,
          };
          console.log('[Config] Configuração carregada do config.json');
          return cachedConfig;
        }
      }
    } catch (error) {
      // Em desenvolvimento, config.json pode não existir
      console.log('[Config] config.json não disponível, usando variáveis de ambiente');
    }

    // Fallback para variáveis de ambiente
    cachedConfig = DEFAULT_CONFIG;
    console.log('[Config] Usando configuração de variáveis de ambiente');
    return cachedConfig;
  })();

  return configLoadPromise;
}

/**
 * Retorna a configuração de forma síncrona.
 * ATENÇÃO: Deve ser chamado após loadRuntimeConfig() ou usar getConfig() assíncrono.
 */
export function getConfigSync(): RuntimeConfig {
  if (!cachedConfig) {
    console.warn('[Config] Configuração não carregada. Usando valores padrão.');
    return DEFAULT_CONFIG;
  }
  return cachedConfig;
}

/**
 * Retorna a configuração de forma assíncrona (recomendado).
 */
export async function getConfig(): Promise<RuntimeConfig> {
  return loadRuntimeConfig();
}

/**
 * Limpa o cache de configuração (útil para testes ou reload forçado).
 */
export function clearConfigCache(): void {
  cachedConfig = null;
  configLoadPromise = null;
}

/**
 * Retorna o token de API.
 */
export async function getApiToken(): Promise<string> {
  const config = await getConfig();
  return config.apiToken;
}

/**
 * Retorna a URL base da API.
 */
export async function getApiUrl(): Promise<string> {
  const config = await getConfig();
  return config.apiUrl.replace(/\/$/, ''); // Remove trailing slash
}
